import React, { useRef, useState } from "react";
import clsx from "clsx";

import IconButton from "@material-ui/core/IconButton";
import DotsIcon from "@material-ui/icons/MoreHoriz";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import EditIcon from "@material-ui/icons/Edit";
import Popover from "@material-ui/core/Popover";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import { useRacehorse360Api } from "hooks/api";
import { useSnackbar } from "components/SnackbarContext/SnackbarContext";
import ConfirmationPopup from "components/ConfirmationPopup";
import { getWorkOrderStatus } from "../../helper";
import useStyles from "./styles";

interface IProps {
  workOrder: racehorse360.IWorkOrder;
  onUpdate?: () => void;
  onEditClick?: () => void;
  type: "popover" | "plain";
}

const WorkOrderActions = React.memo((props: IProps) => {
  const { workOrder, onUpdate, type, onEditClick } = props;
  const classes = useStyles();
  const { showSuccessSnack } = useSnackbar();
  const {
    isOpenedWorkOrder,
    isClosedWorkOrder,
    isCompletedWorkOrder,
    isAssignedWorkOrder,
    isCanceledWorkOrder
  } = getWorkOrderStatus(workOrder);
  const dotsRef = useRef<HTMLButtonElement>();
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false);

  const { useCancelWorkOrder } = useRacehorse360Api();
  const { mutateAsync: cancelWorkOrder, isPending: isCanceling } =
    useCancelWorkOrder();

  const handleDotsClick = () => {
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const handleCancelClick = () => {
    setIsPopoverOpen(false);
    setIsCancelDialogOpen(true);
  };

  const handleCancelDialogClose = () => {
    setIsCancelDialogOpen(false);
  };

  const handleCancelConfirm = () => {
    return cancelWorkOrder({
      id: workOrder.id
    })
      .then(() => {
        setIsCancelDialogOpen(false);
        showSuccessSnack(
          `Work Order #${workOrder.customerFacingId} has been canceled.`
        );
        onUpdate();
      })
      .catch(error => {
        console.error(error);
      });
  };

  const handleEditClick = () => {
    onEditClick();
    setIsPopoverOpen(false);
  };

  if (!workOrder) {
    return <></>;
  }

  const isEditable = ![
    racehorse360.WorkOrderStatus.WORK_ORDER_STATUS_CANCELED,
    racehorse360.WorkOrderStatus.WORK_ORDER_STATUS_COMPLETE,
    racehorse360.WorkOrderStatus.WORK_ORDER_STATUS_CLOSED
  ].includes(workOrder?.status);

  return (
    <>
      {type === "popover" ? (
        <>
          <IconButton
            ref={dotsRef}
            size={"small"}
            disabled={isCanceledWorkOrder}
            className={clsx(classes.dotsButton, {
              [classes.dotsButtonActive]: isPopoverOpen
            })}
            onClick={handleDotsClick}
          >
            <DotsIcon />
          </IconButton>
          <Popover
            anchorEl={dotsRef.current}
            onClose={handlePopoverClose}
            open={isPopoverOpen}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
          >
            <List className={classes.menu}>
              <ListItem
                button
                className={classes.menuItem}
                onClick={handleEditClick}
                disabled={!isEditable}
              >
                <ListItemIcon className={classes.menuItemIcon}>
                  <EditIcon className={classes.editIcon} />
                </ListItemIcon>
                <ListItemText
                  classes={{
                    root: classes.listItemText
                  }}
                  primary="Edit Work Order"
                />
              </ListItem>

              <Divider className={classes.menuDivider} />

              <ListItem
                button
                className={classes.menuItem}
                onClick={handleCancelClick}
                disabled={isCompletedWorkOrder || isClosedWorkOrder}
              >
                <ListItemIcon className={classes.menuItemIcon}>
                  <DeleteForeverIcon className={classes.deleteIcon} />
                </ListItemIcon>
                <ListItemText
                  classes={{
                    root: classes.listItemText
                  }}
                  primary="Cancel Work Order"
                />
              </ListItem>
            </List>
          </Popover>
        </>
      ) : (
        <>
          <Button
            variant={"outlined"}
            className={clsx(classes.button, classes.alert)}
            onClick={handleCancelClick}
            disabled={!isOpenedWorkOrder && !isAssignedWorkOrder}
          >
            CANCEL ORDER
          </Button>
          <Button
            className={clsx(classes.button, classes.editButton)}
            variant={"outlined"}
            onClick={onEditClick}
            disabled={!isEditable}
          >
            EDIT
          </Button>
        </>
      )}
      {isCancelDialogOpen && (
        <ConfirmationPopup
          open={isCancelDialogOpen}
          title={"Cancel Work Order?"}
          cancelButtonText={"Back"}
          confirmButtonText={"Cancel Order"}
          onClose={handleCancelDialogClose}
          onCancel={handleCancelDialogClose}
          onConfirm={handleCancelConfirm}
          isLoading={isCanceling}
          positive={false}
        >
          This work order will no longer be listed as an issue for the track
          maintenance staff.
        </ConfirmationPopup>
      )}
    </>
  );
});

export default WorkOrderActions;
